import { __assign, __decorate, __metadata, __rest } from "tslib";
import { createHttpClient, } from '@wix/http-client';
import { inject, injectable } from 'inversify';
import 'reflect-metadata';
import { of, throwError } from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/fromPromise';
import { catchError, map } from 'rxjs/operators';
import { apiServices } from '../../ioc/services';
import { getBaseUrl } from '../../utils/getBaseUrl';
import { deepMapKeys } from '../../utils/object';
import { toCamelCase, toSnakeCase } from '../../utils/string';
import { FontDto } from './dto/FontDto';
import { GetJobByFileIdDto } from './dto/GetJobByFileIdDto';
import { GetJobBySessionIdDto } from './dto/GetJobBySessionIdDto';
import { JobStatusDto } from './dto/JobStatusDto';
import { MetaDataDto } from './dto/MetaDataDto';
import { MusicTrackDto } from './dto/MusicTrackDto';
import { SaveVideoDto } from './dto/SaveVideoDto';
import { SaveVideoProgressDto } from './dto/SaveVideoProgressDto';
import { ThemeDto } from './dto/ThemeDto';
import { Job } from './Job';
import { JobStatus } from './JobStatus';
import { OutOfQuotaError } from './OutOfQuotaError';
import { Mandatory } from './types/CreateVideoRequest';
/**
 * Api to get available fonts, music, music tags, themes.
 * Create preview video and render high quality video from preview.
 * Get status for single video.
 */
var VideoMakerApi = /** @class */ (function () {
    function VideoMakerApi() {
        this.baseUrl = getBaseUrl();
        this.httpClient = createHttpClient();
    }
    /**
     * Create video preview. After executing Jobs will be created.
     * To poll for job status please use VideoMakerJobQueue.addJob
     *
     * @param request Options for creating video.
     * @returns Array of Job. Size of array is equa to `variations` value in request
     *
     */
    VideoMakerApi.prototype.createVideo = function (request) {
        var siteToken = request.siteToken, otherRequest = __rest(request, ["siteToken"]);
        return this._makeRequest({
            url: '/video',
            method: 'POST',
            data: this._normalizeCreateRequest(otherRequest),
            headers: this._getAuthHeaders({ siteToken: siteToken }),
        }).pipe(map(function (payload) { return payload.videoSessions; }), map(function (videoSessions) {
            return videoSessions.map(function (videoSession) { return new Job({ videoSession: videoSession }); });
        }));
    };
    /**
     * Get status of a job.
     * @param job Job for which status is requested
     * @returns Job status
     */
    VideoMakerApi.prototype.getStatus = function (job) {
        return this._makeRequest({
            url: "/video/".concat(encodeURIComponent("".concat(job.sessionId))),
        }).pipe(map(function (payload) {
            var previewProgress = payload.previewProgress;
            var progress = (previewProgress || 0) / 100;
            return new JobStatusDto(job, __assign(__assign({}, payload), { progress: progress }));
        }), catchError(function (reason) {
            return of(new JobStatusDto(job, { sessionStatus: JobStatus.ERROR, reason: reason }));
        }));
    };
    /**
     * Get fonts that can be used in video
     */
    VideoMakerApi.prototype.getFonts = function () {
        return this._makeRequest({ url: '/fonts' }).pipe(map(function (resp) { return resp.fonts.map(function (font) { return new FontDto(font); }); }));
    };
    /**
     * Get all music tags, that can be found in music
     */
    VideoMakerApi.prototype.getMusicTags = function () {
        return this._makeRequest({
            url: '/music/tags',
        }).pipe(map(function (resp) { return resp.tags; }));
    };
    /**
     * Get available music tracks for video. Response contains `noMusicTrackId` field.
     * Use track with that id if you want no music in video.
     *
     * If themeId OR tags are provided response will be filtered accordingly
     *
     * @returns Tracks and noMusicTrackId
     */
    VideoMakerApi.prototype.getMusicTracks = function (_a) {
        var _b = _a === void 0 ? {} : _a, themeId = _b.themeId, tags = _b.tags;
        if (tags && themeId) {
            throw new Error("\"getMusicTracks\" accepts \"themeId\" OR \"tags\" parameter");
        }
        var params = tags
            ? { tags: tags.map(encodeURIComponent).join(',') }
            : { theme_id: themeId };
        return this._makeRequest({
            url: tags || themeId ? '/music/tracks' : '/music/tracks/all',
            params: params,
        }).pipe(map(function (resp) { return ({
            noMusicTrackId: resp.noMusicTrackId,
            tracks: resp.tracks.map(function (track) { return new MusicTrackDto(track); }),
        }); }));
    };
    /**
     * Get available themes for video
     */
    VideoMakerApi.prototype.getThemes = function () {
        return this._makeRequest({ url: '/music/themes' }).pipe(map(function (resp) {
            return resp.themes.map(function (theme) { return ThemeDto.fromThemesPayload(theme); });
        }));
    };
    /**
     * Render high quality video from previously created preview.
     * To get status of render use VideoMakerJobQueue.waitForStreamUrl
     * If saveTo parameter is specified, video will be saved to users/site files.
     * To get status of save use VideoMakerJobQueue.getFileName
     */
    VideoMakerApi.prototype.renderVideo = function (request) {
        var siteToken = request.siteToken, _a = request.withExperiment, withExperiment = _a === void 0 ? false : _a, restRequest = __rest(request, ["siteToken", "withExperiment"]);
        return this._makeRequest({
            url: withExperiment ? 'video/render_with_experiment' : '/video/render',
            method: 'POST',
            data: restRequest,
            headers: this._getAuthHeaders({ siteToken: siteToken }),
        }).pipe(catchError(function (error) {
            var _a, _b, _c;
            if (((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.payload) === null || _c === void 0 ? void 0 : _c.code) === 'OVER_QUOTA') {
                return throwError(new OutOfQuotaError('quota has been exceeded', error));
            }
            return throwError(error);
        }), map(function (payload) { return new SaveVideoDto(payload); }));
    };
    /**
     * Save video from previously created preview.
     * for quality param possible values are: "sd", "fullhd" (default)
     */
    VideoMakerApi.prototype.saveVideo = function (request) {
        var siteToken = request.siteToken, restRequest = __rest(request, ["siteToken"]);
        return this._makeRequest({
            url: '/video/save',
            method: 'POST',
            data: restRequest,
            headers: this._getAuthHeaders({ siteToken: siteToken }),
        }).pipe(map(function (payload) { return new SaveVideoDto(payload); }));
    };
    /**
     * Get progress data while VideoMakerJobQueue.waitForStreamUrl is working
     */
    VideoMakerApi.prototype.getSavingVideoProgress = function (progressId) {
        return this._makeRequest({
            url: '/video/progress',
            params: {
                progressId: progressId,
            },
        }).pipe(map(function (resp) { return SaveVideoProgressDto.fromSavingVideoProgressPayload(resp); }));
    };
    /**
     * Return previously created previews
     * @private
     */
    VideoMakerApi.prototype.getMetaData = function (metaSiteId) {
        return this._makeRequest({
            url: "video/jobs",
            params: {
                siteId: metaSiteId,
                limit: 1,
            },
        }).pipe(map(function (resp) {
            var data = resp.jobs && resp.jobs[0] && resp.jobs[0].data;
            if (!data) {
                return;
            }
            var lastRequest = deepMapKeys(data, function (s) {
                return typeof s === 'string' ? toCamelCase(s) : s;
            });
            var _a = lastRequest.business, business = _a === void 0 ? {} : _a, themeId = lastRequest.themeId;
            return new MetaDataDto({
                font: business.font,
                mainColor: business.primaryColor,
                secondaryColor: business.secondaryColor,
                themeId: themeId,
            });
        }));
    };
    /**
     * Get Job by file id
     * Return previously created job when file was saved to site-files by fileId
     */
    VideoMakerApi.prototype.getJobByFileId = function (fileId, siteToken) {
        return this._makeRequest({
            url: '/video/job',
            method: 'GET',
            params: {
                fileName: fileId,
                siteToken: siteToken,
            },
        }).pipe(map(function (payload) {
            return GetJobByFileIdDto.fromGetJobByFileIdResponse(deepMapKeys(payload, function (s) {
                return typeof s === 'string' ? toCamelCase(s) : s;
            }));
        }));
    };
    /**
     * Get Job by session id
     * Return previously created job by sessionId
     */
    VideoMakerApi.prototype.getJobBySessionId = function (sessionId) {
        return this._makeRequest({
            url: "/video/".concat(encodeURIComponent("".concat(sessionId))),
        }).pipe(map(function (payload) { return new GetJobBySessionIdDto(payload); }));
    };
    VideoMakerApi.prototype._makeRequest = function (config) {
        return fromPromise(this.httpClient
            .request(__assign({ withCredentials: true, xsrfCookieName: "".concat(Date.now(), "-").concat(Math.round(Math.random() * Math.pow(10, 10))), baseURL: this.baseUrl }, config))
            .then(function (resp) { return resp.data.payload; }));
    };
    VideoMakerApi.prototype._normalizeCreateRequest = function (request) {
        var _a;
        var sources = request.sources.map(function (_a) {
            var mandatory = _a.mandatory, otherFields = __rest(_a, ["mandatory"]);
            return mandatory === Mandatory.No ? otherFields : __assign({ mandatory: mandatory }, otherFields);
        });
        var scriptData = request.scriptData, script = request.script;
        return deepMapKeys(__assign(__assign({}, request), { sources: sources, scriptData: (_a = {}, _a[script] = scriptData, _a) }), function (s) { return (typeof s === 'string' ? toSnakeCase(s) : s); });
    };
    VideoMakerApi.prototype._getAuthHeaders = function (_a) {
        var _b = _a === void 0 ? {} : _a, siteToken = _b.siteToken;
        return siteToken ? { Authorization: siteToken } : undefined;
    };
    __decorate([
        inject(apiServices.videoMakerBaseUrl),
        __metadata("design:type", String)
    ], VideoMakerApi.prototype, "baseUrl", void 0);
    VideoMakerApi = __decorate([
        injectable()
    ], VideoMakerApi);
    return VideoMakerApi;
}());
export { VideoMakerApi };
//# sourceMappingURL=VideoMakerApi.js.map